import React, { useRef, useState, useEffect, useCallback, memo } from 'react';
import { ChevronDown } from '@saleshandy/icons';
import { accessibleOnClick } from '../../utils/accessible-on-click';
import { AccordionProps } from './type';
import { adjustOverlayPosition } from '../../../components/sequence/components/sequence-list-content/components/sequence-list-table/helper';
import {
  formatNumberToKM,
  formatPercentage,
} from '../../../components/sequence/helpers/helper';
import NoResultFound from '../../svg/no-result-found';

/**
 *
 * @param data - Data to be displayed in the progress card
 * @param borderClassName - Border color for the indicator status in progress card
 * @param className - Custom class name for the progress card
 * @param isLoading - Boolean flag to determine the loading state
 * @param handleHideOverlayCard - fun to hide the overlay card
 */

const ProgressCard: React.FC<AccordionProps> = memo(
  ({
    data = [],
    borderClassName = '#93C5FD',
    className = '',
    isLoading = false,
    handleHideOverlayCard = () => {},
  }) => {
    const [openAccordions, setOpenAccordions] = useState<string[]>([]);
    const progressRef = useRef<HTMLDivElement>(null);

    const toggleAccordion = useCallback((title: string) => {
      setOpenAccordions((prev) => {
        const isClosing = prev?.includes(title);
        const newState = isClosing
          ? prev.filter((item) => item !== title)
          : [...prev, title];

        // Wait for the transition to complete (400ms) in both cases
        setTimeout(() => {
          adjustOverlayPosition(`.${className}`);
        }, 500);

        return newState;
      });
    }, []);

    const handleClickAndScrollOutside = useCallback(
      (event: MouseEvent | Event) => {
        if (
          progressRef?.current &&
          !progressRef?.current?.contains(event.target as Node)
        ) {
          handleHideOverlayCard();
        }
      },
      [handleHideOverlayCard, progressRef],
    );

    useEffect(() => {
      document.addEventListener('mousedown', handleClickAndScrollOutside);
      document.addEventListener('scroll', handleClickAndScrollOutside, true);

      return () => {
        document.removeEventListener('mousedown', handleClickAndScrollOutside);
        document.removeEventListener(
          'scroll',
          handleClickAndScrollOutside,
          true,
        );
      };
    }, [handleClickAndScrollOutside]);

    return (
      <div className={`progress-container ${className}`} ref={progressRef}>
        {!isLoading && data?.length === 0 && (
          <div className="no-results-found-container">
            <div className="no-results-found">
              <NoResultFound />
              <span>No results found</span>
            </div>
          </div>
        )}

        {!isLoading &&
          data?.length > 0 &&
          data.map((option, index) => {
            const isChildrenAccordionOpen = openAccordions.includes(
              option?.title,
            );
            return (
              <>
                <div
                  key={`childrenAccordion-${index + 1}`}
                  className="progress-main-card"
                >
                  <div className="progress-single-tab">
                    <div className="progress-title-bar">
                      <span
                        className="progress-rectangle-border"
                        style={{ backgroundColor: `${borderClassName}` }}
                      />
                      {option?.icon && (
                        <span className="progress-title-icon">
                          {option?.icon}
                        </span>
                      )}
                      {option.childrenAccordion ? (
                        <div
                          {...accessibleOnClick(() =>
                            toggleAccordion(option?.title),
                          )}
                          className="accordion-title"
                        >
                          <span className="progress-title-text">
                            {option?.title || ''}
                          </span>
                          <ChevronDown
                            className={`chevron-icon-down ${
                              isChildrenAccordionOpen ? 'rotate' : ''
                            }`}
                          />
                        </div>
                      ) : (
                        <span className="progress-title-text">
                          {option?.title || ''}
                        </span>
                      )}
                    </div>

                    <div className="progress-score">
                      <span className="progress-main-score">
                        {formatNumberToKM(Number(option?.score) || 0)}
                      </span>
                      <span className="progress-percentage">
                        {Number(option?.percentage) > 0
                          ? `${formatPercentage(option?.percentage)}%`
                          : ''}
                      </span>
                    </div>
                  </div>

                  <div
                    className={`accordion-container-popup ${
                      isChildrenAccordionOpen ? 'open' : ''
                    }`}
                  >
                    {option?.childrenAccordion?.map((accordionData) => (
                      <div
                        key={accordionData?.title}
                        className="accordion-main-card"
                      >
                        <div className="accordion-tab-bar">
                          <div className="accordion-single-tab">
                            <div className="accordion-title-bar">
                              <span className="accordion-rectangle-border" />
                              <span className="accordion-title-text">
                                {accordionData?.title || ''}
                              </span>
                            </div>
                            <div className="accordion-score">
                              <span className="accordion-main-score">
                                {formatNumberToKM(
                                  Number(accordionData?.score) || 0,
                                )}
                              </span>
                              <span className="accordion-percentage">
                                {Number(accordionData?.percentage) > 0
                                  ? `${formatPercentage(
                                      accordionData.percentage,
                                    )}%`
                                  : ''}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            );
          })}
      </div>
    );
  },
);

export default ProgressCard;
